/* eslint-disable consistent-return */
import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import { Logger, LoggerTypes } from '../../utils'
import { Button, buttonModes, buttonTypes } from '..'
import { variantTypes } from '../../constants'

/**
 * Display a simple popin to edit only one field
 *
 * @param {object} param Ex: {
 *                          openDialog: true // hook value
 *                          setOpenDialog: () => { ... }  // React hook function
 *                          value: 'field value...'
 *                          onSave: (string) => value // where value is the new name
 *                        }
 * @returns {Dialog}
 */
export const DialogFieldEdit = ({ openDialog, setOpenDialog, value, onSave, className }) => {
  const { t } = useTranslation()
  const [fieldValue, setFieldValue] = useState(value)
  const inputRef = useRef()

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current.focus()
    }, 100)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  const onCloseDialog = () => setOpenDialog(false)
  const onChangeField = (e) => setFieldValue(e.target.value)

  const onClickNo = (e) => {
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'DialogFieldEdit.onClickNo()', { value })

    onCloseDialog()
  }

  const onClickYes = (e) => {
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'DialogFieldEdit.onClickYes()', { fieldValue })

    onCloseDialog()

    if (typeof onSave === 'function') {
      onSave(fieldValue)
    }
  }

  Logger.trace(LoggerTypes.CONTAINER, 'DialogFieldEdit.render()', { openDialog })

  return (
    <Dialog
      open={openDialog}
      // onClose={onCloseDialog} // <-- close while clicking on background
      aria-labelledby="form-dialog-title"
      className={`${className}`}
    >
      <DialogTitle id="form-dialog-title">{t('common.dialogs.update.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('common.dialogs.update.message')}</DialogContentText>
        <TextField
          value={fieldValue}
          onChange={onChangeField}
          variant={variantTypes.STANDARD}
          placeholder={t('common.placeholders.name')}
          className="common_fullwidth"
          inputRef={inputRef}
        ></TextField>
      </DialogContent>
      <DialogActions>
        <Button
          mode={buttonModes.SECONDARY_ACTION}
          type={buttonTypes.BUTTON}
          text={t('common.cancel')}
          icon={<CloseIcon />}
          onClick={onClickNo}
        />
        <Button
          mode={buttonModes.PRIMARY_ACTION}
          type={buttonTypes.BUTTON}
          text={t('common.save')}
          icon={<SaveOutlinedIcon />}
          onClick={onClickYes}
        />
      </DialogActions>
    </Dialog>
  )
}

DialogFieldEdit.displayName = 'DialogFieldEdit'

DialogFieldEdit.propTypes = {
  className: PropTypes.string,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired
}

DialogFieldEdit.defaultProps = {
  className: '',
  openDialog: false,
  setOpenDialog: () => {},
  value: '',
  onSave: null
}
